import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import { Column } from 'react-table';

import { Table, TableSkeleton } from '@/lib/components';
import { SortTableArrow } from '@/lib/components/SortTableArrow';
import { Flex, IconSvg } from '@/lib/v2/components';
import { LoadingIcon } from '@/lib/v2/icons/animated';
import { DownloadIcon } from '@/lib/v2/icons/general/DownloadIcon';
import { EmptyScreenTable } from '@/lib/v2/organisms';

import noInvoice from '@/src/assets/lottiefiles/no-invoices.json';
import { HansaInvoice } from '@/src/modules/MyPlanModule/types';
import { numberToCurrency } from '@/src/utils/currency';

import { DateFormat, formatDate } from '@/modules/CampaignsModule/utils';

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: noInvoice,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

interface LegalBillingTableProps {
  legalBillingList?: HansaInvoice[];
  isLoadingLegalBillingList: boolean;
  totalLegalBillingList: number;
  totalPages: number;
  changeTablePage: (params: { pageSize: number; pageIndex: number }) => void;
  changeTableOrder: (element: { id: string; isAsc: boolean | undefined }) => void;
  onDownload: (serialNumber: number) => Promise<void>;
  downloadingSerialNumbers: Set<number>;
  error: Error | undefined;
}

const LegalBillingTable = ({
  legalBillingList,
  isLoadingLegalBillingList: legalBillingListIsLoading,
  totalLegalBillingList,
  totalPages,
  changeTablePage,
  changeTableOrder,
  onDownload,
  error,
  downloadingSerialNumbers,
}: LegalBillingTableProps) => {
  const { t } = useTranslation();

  const initialStateOrderByList = [{ id: 'invoiceDate', isAsc: false }];

  const [tableOrderByList, setTableOrderByList] =
    useState<Array<{ id: string; isAsc: boolean | undefined }>>(initialStateOrderByList);

  // Memoize the empty screen configuration
  const emptyScreenConfig = useMemo(
    () => ({
      totalCount: totalLegalBillingList || 0,
      noResult: <EmptyScreenTable title={t('EMPTY_SCREEN_TABLE.emptyNoteTitle')} />,
      noData: (
        <EmptyScreenTable
          image={<Lottie options={defaultOptions} width={150} />}
          title={t('MY_PLAN.LEGAL_BILLING.noInvoices')}
        />
      ),
      perms: error ? 'error' : undefined,
      noPerms: (
        <EmptyScreenTable
          image={<Lottie options={defaultOptions} width={150} />}
          title={t('MY_PLAN.LEGAL_BILLING.error')}
        />
      ),
    }),
    [error, t, totalLegalBillingList]
  );

  // Memoize the columns to prevent unnecessary re-renders
  const legalBillingTableColumns = useMemo(
    () =>
      [
        {
          Header: () => (
            <Flex withGap alignment="start">
              <span className="font-medium text-emblue-primary">
                {t('MY_PLAN.LEGAL_BILLING.invoiceDate')}
              </span>
              <SortTableArrow
                elementName={'invoiceDate'}
                getTableOrderByList={changeTableOrder}
                setTableOrderByList={setTableOrderByList}
                tableOrderByList={tableOrderByList}
              />
            </Flex>
          ),
          accessor: 'invoiceDate',
          id: 'invoiceDate',
          width: '20%',
          Cell: ({ cell: { value } }: { cell: { value: string } }) => (
            <div className="flex">
              <span className="grow-0 self-start text-14 text-[#364365]">
                {formatDate(value, DateFormat.ShortDate)}
              </span>
            </div>
          ),
        },
        {
          Header: () => (
            <Flex withGap alignment="start">
              <span className="font-medium text-emblue-primary">
                {t('MY_PLAN.LEGAL_BILLING.invoiceDueDate')}
              </span>
            </Flex>
          ),
          accessor: 'invoiceDueDate',
          id: 'invoiceDueDate',
          width: '20%',
          Cell: ({
            cell: {
              row: { original },
            },
          }: {
            cell: { row: { original: HansaInvoice } };
          }) => (
            <Flex withGap alignment="start">
              <span className="self-start text-14 text-[#364365]">
                {original.typeOfInvoice === 'credit_note'
                  ? '-'
                  : formatDate(original.invoiceDueDate, DateFormat.ShortDate)}
              </span>
            </Flex>
          ),
        },
        {
          Header: () => (
            <Flex withGap alignment="start">
              <span className="font-medium text-emblue-primary">
                {t('MY_PLAN.LEGAL_BILLING.invoiceOfficialSerialNumber')}
              </span>
            </Flex>
          ),
          accessor: 'invoiceOfficialSerialNumber',
          id: 'invoiceOfficialSerialNumber',
          width: '20%',
          Cell: ({
            cell: { value },
          }: {
            cell: {
              value: string;
            };
          }) => (
            <Flex withGap alignment="start">
              <span className="self-start text-14 text-[#364365]">{value}</span>
            </Flex>
          ),
        },
        {
          Header: () => (
            <Flex withGap alignment="start">
              <span className="font-medium text-emblue-primary">
                {t('MY_PLAN.LEGAL_BILLING.typeOfInvoice')}
              </span>
            </Flex>
          ),
          accessor: 'typeOfInvoice',
          id: 'typeOfInvoice',
          width: '20%',
          Cell: ({
            cell: { value },
          }: {
            cell: {
              value: string;
            };
          }) => (
            <Flex withGap alignment="start">
              <span className="self-start text-14 text-[#364365]">
                {t(`MY_PLAN.LEGAL_BILLING.typeOfInvoice-${value}`)}
              </span>
            </Flex>
          ),
        },
        {
          Header: () => (
            <Flex withGap alignment="start">
              <span className="font-medium text-emblue-primary">
                {t('MY_PLAN.LEGAL_BILLING.amount')}
              </span>
            </Flex>
          ),
          accessor: 'invoiceFinalAmount',
          id: 'invoiceFinalAmount',
          width: '20%',
          Cell: ({
            cell: {
              value,
              row: { original },
            },
          }: {
            cell: {
              value: number;
              row: { original: HansaInvoice };
            };
          }) => (
            <Flex withGap alignment="start">
              <span className="self-start text-14 text-[#364365]">
                {original.currencyCode} {numberToCurrency(value, true)}
              </span>
            </Flex>
          ),
        },
        {
          Header: () => (
            <Flex withGap alignment="start">
              <span className="font-medium text-emblue-primary">
                {t('MY_PLAN.LEGAL_BILLING.attachment')}
              </span>
            </Flex>
          ),
          accessor: 'hasInvoiceAttachment',
          id: 'hasInvoiceAttachment',
          width: '20%',
          Cell: ({
            cell: {
              row: { original },
            },
          }: {
            cell: { row: { original: HansaInvoice } };
          }) => {
            const isRowDownloading = downloadingSerialNumbers.has(original.serialNumber);
            return (
              <Flex withGap alignment="start">
                {isRowDownloading ? (
                  <LoadingIcon />
                ) : (
                  <IconSvg
                    className={
                      original.hasInvoiceAttachment ? 'cursor-pointer' : 'cursor-not-allowed'
                    }
                    fillColor={original.hasInvoiceAttachment ? 'primary' : 'disabled'}
                    height="20px"
                    svgComponent={<DownloadIcon />}
                    width="20px"
                    onClick={() => {
                      if (original.hasInvoiceAttachment) {
                        void onDownload(original.serialNumber);
                      }
                    }}
                  />
                )}
              </Flex>
            );
          },
        },
      ].filter((x) => x) as Column<object>[],
    [t, changeTableOrder, downloadingSerialNumbers, onDownload, tableOrderByList]
  );

  if (!legalBillingList && error === undefined) {
    return (
      <div className="size-full py-10">
        <TableSkeleton />
      </div>
    );
  }

  return (
    <Table
      isPaginateOnOverflow
      classNamePagination="pr-4"
      columns={legalBillingTableColumns}
      data={legalBillingList || []}
      emptyScreen={emptyScreenConfig}
      isLoading={legalBillingListIsLoading}
      withCheckbox={false}
      withPagination={{
        totalPages,
        fetchData: changeTablePage,
      }}
    />
  );
};

export default LegalBillingTable;
